import { Config } from '@/Config';
import {
  merchandiseReleasesGridUrl,
  merchandiseSubmissionsGridUrl,
  releasesGridUrl,
  submissionsGridUrl,
} from '@/components/common/Grid/GridUtilities';
import { FormatType } from '@/data/models/formatType';
import { getAuth } from '@/selectors/auth';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import CreateProduct from '../CreateProduct/CreateProduct';
import styles from './SideNavigation.module.scss';
import './SideNavigation.scss';

interface Props {
  sideNavOpen: boolean;
  toggleSideNav: () => void;
}

function SideNavigation({ sideNavOpen, toggleSideNav }: Props) {
  const [currentLocation, setCurrentLocation] = useState('');
  const [showCreateProduct, setShowCreateProduct] = useState(false);
  const { t } = useTranslation();
  const { hasWriteAccess } = useSelector(getAuth);

  const swtichMenuEnv = ['DEV', 'CI', 'QA'];
  const releasesPaths = ['Physical/Releases', ''];
  const submissionsPaths = ['Physical/Submissions', 'Physical/Products'];
  const merchandisePaths = ['Merchandise/Submissions', 'Merchandise/Products'];
  const merchandiseReleasesPaths = ['Merchandise/Releases'];

  const location = useLocation();

  useEffect(() => {
    const parts = location.pathname.split('/');
    const firstPart = parts[1];
    const secondPart = parts[2];
    const twoParts = `${firstPart}/${secondPart}`;
    const allPaths = [
      ...releasesPaths,
      ...submissionsPaths,
      ...merchandisePaths,
      ...merchandiseReleasesPaths,
    ];

    if (allPaths.includes(twoParts)) {
      setCurrentLocation(twoParts);
    } else {
      setCurrentLocation(firstPart || '');
    }
  }, [location]);

  const preventActiveGridNavigation =
    (isActive: boolean) => (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (isActive) {
        event.preventDefault();
      }
    };

  return (
    <div id="mySidenav" className="sidenav">
      <ul>
        {!currentLocation.startsWith('Merchandise') && (
          <>
            <li>
              <Link
                to={releasesGridUrl}
                onClick={preventActiveGridNavigation(
                  releasesPaths.includes(currentLocation)
                )}
                className={
                  releasesPaths.includes(currentLocation) ? 'active' : ''
                }
              >
                <span className="icon fas fa-music"></span>
                <span className="item-text nav-text">
                  {t('menu.sideNav.releases')}
                </span>
              </Link>
            </li>
            <li>
              <Link
                to={submissionsGridUrl}
                onClick={preventActiveGridNavigation(
                  submissionsPaths.includes(currentLocation)
                )}
                className={
                  submissionsPaths.includes(currentLocation) ? 'active' : ''
                }
              >
                <span className="icon fas fa-share-square"></span>
                <span className="item-text nav-text">
                  {t('menu.sideNav.submissions')}
                </span>
              </Link>
            </li>
          </>
        )}

        {currentLocation.startsWith('Merchandise') && (
          <>
            <li>
              <Link
                to={merchandiseReleasesGridUrl}
                onClick={preventActiveGridNavigation(
                  merchandiseReleasesPaths.includes(currentLocation)
                )}
                className={
                  merchandiseReleasesPaths.includes(currentLocation)
                    ? 'active'
                    : ''
                }
              >
                <span className="icon fas fa-bag-shopping"></span>
                <span className="item-text nav-text">
                  {t('menu.sideNav.releases')}
                </span>
              </Link>
            </li>
            <li>
              <Link
                to={merchandiseSubmissionsGridUrl}
                onClick={preventActiveGridNavigation(
                  merchandisePaths.includes(currentLocation)
                )}
                className={
                  merchandisePaths.includes(currentLocation) ? 'active' : ''
                }
              >
                <span className="icon fas fa-share-square"></span>
                <span className="item-text nav-text">
                  {t('menu.sideNav.submissions')}
                </span>
              </Link>
            </li>
          </>
        )}

        {hasWriteAccess ? (
          <>
            <div className={styles.menuDivider}></div>
            <li>
              <a
                className={styles.createProductLink}
                onClick={() => setShowCreateProduct(true)}
              >
                <span className="icon fas fa-plus"></span>
                <span className="item-text nav-text">
                  {t('product.createProduct')}
                </span>
              </a>
            </li>
          </>
        ) : null}
      </ul>

      {swtichMenuEnv.includes(Config.ENVIRONMENT) && (
        <ul className="sidenav-switch">
          <li>
            {currentLocation.startsWith('Merchandise') ? (
              <Link to={releasesGridUrl}>
                <span className="icon fas fa-music"></span>
                <span className="item-text nav-text">
                  {t('menu.sideNav.pyhisical')}
                </span>
              </Link>
            ) : (
              <Link to={merchandiseReleasesGridUrl}>
                <span className="icon fas fa-bag-shopping"></span>
                <span className="item-text nav-text">
                  {t('menu.sideNav.merchandise')}
                </span>
              </Link>
            )}
          </li>
        </ul>
      )}

      <div className="sidenav-toggle" onClick={toggleSideNav}>
        <span className="nav-icon item-text icon fas fa-thumbtack"></span>
        <span className="nav-text item-text">
          {sideNavOpen
            ? t('menu.sideNav.unpinMenu')
            : t('menu.sideNav.pinMenu')}
        </span>
      </div>

      {showCreateProduct && (
        <CreateProduct
          toggle={showCreateProduct}
          togglePopUp={setShowCreateProduct}
          formatType={
            currentLocation.startsWith('Merchandise')
              ? FormatType.Merchandise
              : FormatType.Physical
          }
        />
      )}
    </div>
  );
}

export default SideNavigation;
